import { GraduationCap, Award, Briefcase, TrendingUp, ChevronRight, Home, CheckCircle2, Star, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Experience = () => {
  const academicQualifications = [
    {
      year: '2015',
      degree: 'MBBS',
      institution:
        'Grant Government Medical College & JJ Group of Hospitals, Mumbai, Maharashtra',
    },
    {
      year: '2018',
      degree: 'MS – General Surgery',
      institution:
        'BJ Medical College & Sassoon Hospitals, Pune, Maharashtra',
    },
    {
      year: '2022',
      degree: 'M.Ch – Urology',
      institution:
        'Government Medical College & Transplant Institute, Kota, Rajasthan',
    },
  ];


  const professionalExperience = [
    {
      period: '2022 – 2023',
      position: 'Consultant Urologist',
      organization: 'BRH & Sanjiveeni Hospital, Jalore',
    },
    {
      period: '2019 – 2022',
      position: 'M.Ch Senior Resident (Urology)',
      organization: 'Government Medical College & Transplant Institute, Kota, Rajasthan',
    },
    {
      period: '2018 – 2019',
      position: 'Senior Resident – Department of General Surgery',
      organization: 'BJ Medical College & Sassoon Hospitals, Pune, Maharashtra',
    },
  ];

  return (
    <div className="pt-20 bg-[#fbfdff] overflow-hidden">

      {/* --- 1. MODERN BREADCRUMB HEADER (Matching About & Services) --- */}
      <section className="bg-gradient-to-br from-blue-900 to-teal-800 py-12 relative">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-teal-400/10 rounded-full -ml-10 -mb-10 blur-2xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="flex items-center gap-2 text-teal-200/80 text-sm font-bold uppercase tracking-widest mb-4">
            <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" /> Home
            </Link>
            <ChevronRight className="w-4 h-4 opacity-50" />
            <span className="text-white">Experience & Qualifications</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">Clinical Experience</h1>
          <div className="h-1.5 w-24 bg-teal-400 mt-4 rounded-full" />
          <p className="text-blue-100/70 mt-6 max-w-2xl font-medium text-lg">
            A distinguished career of 8+ years dedicated to surgical excellence and patient-centric urological care.
          </p>
        </div>
      </section>

      {/* --- 2. PROFESSIONAL TIMELINE (Clinical Experience) --- */}
      <section className="py-24 bg-white relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-16">
            <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200">
              <Briefcase className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-4xl font-black text-slate-900 italic">Hospital Experience</h2>
          </div>

          <div className="space-y-12 relative before:absolute before:left-[1.7rem] md:before:left-[2.2rem] before:top-2 before:bottom-2 before:w-1 before:bg-slate-100">
            {professionalExperience.map((exp, index) => (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                key={index}
                className="relative pl-16 md:pl-24"
              >
                {/* Timeline Dot */}
                <div className="absolute left-4 md:left-6 top-1 w-6 h-6 rounded-full bg-white border-4 border-blue-600 z-10 shadow-sm" />

                <div className="bg-slate-50 p-8 md:p-10 rounded-[2.5rem] border border-slate-100 hover:bg-white hover:shadow-2xl hover:border-blue-100 transition-all group">
                  <div className="mb-2">
                    <div>
                      <span className="px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                        {exp.period}
                      </span>
                      <h3 className="text-2xl font-black text-slate-900 mt-3 group-hover:text-blue-600 transition-colors">{exp.position}</h3>
                      <p className="text-slate-500 font-bold flex items-center gap-2 mt-1">
                        <Calendar className="w-4 h-4 opacity-40" /> {exp.organization}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 3. ACADEMIC JOURNEY (Grid) --- */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-white to-transparent opacity-100" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <GraduationCap className="w-12 h-12 text-blue-400 mx-auto mb-6" />
            <h2 className="text-4xl font-black italic mb-4">Educational Foundations</h2>
            <p className="text-blue-200/50 font-bold tracking-[0.2em] uppercase text-xs">World-class specialized training</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {academicQualifications.map((qual, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-md p-10 rounded-[3rem] border border-white/10 hover:bg-white/10 transition-all group">
                <div className="text-blue-400 font-black text-2xl mb-4 opacity-50 group-hover:opacity-100 transition-opacity italic">{qual.year}</div>
                <h3 className="text-xl font-bold mb-4 leading-tight">{qual.degree}</h3>
                <p className="text-blue-300 font-bold text-xs uppercase tracking-wider">{qual.institution}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 5. CTA SECTION --- */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 text-center bg-gradient-to-br from-blue-900 to-teal-800 p-16 rounded-[4rem] text-white shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
          <div className="relative z-10">
            <h2 className="text-4xl font-black mb-6 italic">Trust Your Health to Experience</h2>
            <p className="text-blue-100 text-lg font-medium mb-10 max-w-xl mx-auto opacity-80">
              Committed to providing advanced and compassionate urological care, your care is in expert hands.
            </p>
            <Link to="/appointment" className="inline-flex items-center gap-3 bg-white text-blue-900 px-12 py-5 rounded-2xl font-black text-lg hover:bg-blue-50 transition-all shadow-xl">
              Consult Dr. Chaurasia <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Experience;